
import Vue from 'vue'
import AlertBanner from '@/components/AlertBanner.vue'
import AppModal from '@/components/AppModal.vue'
import ConsentTitle from '@/components/ConsentGrantPage/ConsentTitle.vue'
import ConsentStatusCard from '@/components/ConsentGrantPage/ConsentStatusCard.vue'
import ConsentRequestForm from '@/components/ConsentGrantPage/ConsentRequestForm.vue'
import type { ConsentForm } from '@/components/ConsentGrantPage/ConsentRequestForm.vue'
import Service from '@/services'
import type { ConsentStatus, ConsentSubmitPayload } from '@/services'
import { DEFAULT_DOMAIN } from '@/constants/domain'
import {
	ACTIONABLE_STATUSES,
	DEFAULT_CONSENT_DATA,
	getErrorMessage,
	getStatusVariant,
	normalizeConsentData,
	resolveSubmitMessage,
	type ConsentData,
	type SnackbarType
} from '@/utils/consent'

export default Vue.extend({
	name: 'ConsentGrant',
	components: {
		AlertBanner,
		AppModal,
		ConsentStatusCard,
		ConsentRequestForm,
		ConsentTitle
	},
	props: {
		consentCode: { type: String, required: true }
	},
	data() {
		return {
			loading: true,
			fetchError: '',
			consentData: { ...DEFAULT_CONSENT_DATA } as ConsentData,
			form: {
				signatoryNpwp: '',
				authKey: '',
				agreeTc: false
			} as ConsentForm,
			formErrors: {} as Record<string, string>,
			submitLoading: false,
			snackbarActive: false,
			snackbarText: '',
			snackbarType: 'info' as SnackbarType,
			showDeclineModal: false
		}
	},
	computed: {
		isActionable(): boolean {
			return ACTIONABLE_STATUSES.includes(this.consentData.status as ConsentStatus)
		},
		showStatusCard(): boolean {
			return !this.isActionable
		},
		statusVariant() {
			return getStatusVariant(this.consentData.status)
		},
		canSubmit(): boolean {
			return !!(
				this.isActionable &&
				this.form.signatoryNpwp.trim().length >= 15 &&
				this.form.authKey.trim() &&
				this.form.agreeTc
			)
		},
		opDomain(): string {
			return process.env.VUE_APP_OP_DOMAIN || DEFAULT_DOMAIN
		},
		serviceLearnMoreUrl(): string {
			const riskPulseUrl = 'https://app.achilles.id/credor/riskpulse/'
			const serviceUrls: Record<string, string> = {
				RiskPulse: riskPulseUrl,
				'Risk Pulse': riskPulseUrl
			}
			return serviceUrls[this.consentData.serviceName] || `https://www.${this.opDomain}`
		},
		authKeyLearnUrl(): string {
			return 'https://support.achilles.id/en/hc/?help_article=menghubungkan-onlinepajak-dengan-coretax-djp-menggunakan-kunci-autentikasi'
		}
	},
	mounted() {
		this.fetchConsentRequest()
	},
	methods: {
		async fetchConsentRequest(silent = false) {
			if (!silent) this.loading = true
			this.fetchError = ''
			try {
				const data = await Service.getConsentGrantDetails(this.consentCode)
				if (!data) {
					this.fetchError = this.$t('errors.general-error') as string
					return
				}
				this.consentData = normalizeConsentData(data, this.consentData)
			} catch {
				this.fetchError = this.$t('errors.general-error') as string
			} finally {
				if (!silent) this.loading = false
			}
		},
		validateApproveForm(): boolean {
			this.formErrors = {}
			const required = this.$t('errors.field_required') as string
			const signatoryNpwp = this.form.signatoryNpwp.trim()
			const authKey = this.form.authKey.trim()
			if (!signatoryNpwp) this.formErrors.signatoryNpwp = required
			else if (signatoryNpwp.length < 15) {
				this.formErrors.signatoryNpwp = this.$t('errors.npwp__min_length') as string
			}
			if (!authKey) this.formErrors.authKey = required
			if (Object.keys(this.formErrors).length > 0) return false
			if (!this.form.agreeTc) {
				this.showSnackbar(this.$t('errors.tos_not_accepted') as string, 'error')
				return false
			}
			return true
		},
		handleConsentErrorCode(code: number | string | null | undefined): boolean {
			const normalized = String(code ?? '')
			if (normalized === '301') {
				this.renderInvalidLinkState()
				return true
			}
			return false
		},
		async handleApproveSubmit() {
			if (this.submitLoading || !this.validateApproveForm()) return
			this.submitLoading = true
			try {
				const payload: ConsentSubmitPayload = {
					signatoryNpwp: this.form.signatoryNpwp.trim(),
					authKey: this.form.authKey.trim(),
					status: 'APPROVED'
				}
				const response = await Service.consentSubmit(this.consentCode, payload)
				if (this.handleConsentErrorCode(response?.code)) return
				const { text, type } = resolveSubmitMessage(
					response,
					'approve',
					(k) => this.$t(k) as string
				)
				this.showSnackbar(text, type)
				await this.fetchConsentRequest(true)
			} catch (err) {
				const errorCode = (
					err as {
						response?: { data?: { code?: number | string } }
					}
				)?.response?.data?.code
				if (this.handleConsentErrorCode(errorCode)) return
				this.showSnackbar(
					getErrorMessage(err, (k) => this.$t(k) as string),
					'error'
				)
			} finally {
				this.submitLoading = false
			}
		},
		async handleDeclineSubmit() {
			if (this.submitLoading) return
			this.formErrors = {}
			this.submitLoading = true
			try {
				const payload: ConsentSubmitPayload = {
					signatoryNpwp: '',
					authKey: '',
					status: 'DECLINED'
				}
				const response = await Service.consentSubmit(this.consentCode, payload)
				if (this.handleConsentErrorCode(response?.code)) return
				const { text, type } = resolveSubmitMessage(
					response,
					'decline',
					(k) => this.$t(k) as string
				)
				this.showSnackbar(text, type)
				await this.fetchConsentRequest(true)
			} catch (err) {
				const errorCode = (
					err as {
						response?: { data?: { code?: number | string } }
					}
				)?.response?.data?.code
				if (this.handleConsentErrorCode(errorCode)) return
				this.showSnackbar(
					getErrorMessage(err, (k) => this.$t(k) as string),
					'error'
				)
			} finally {
				this.submitLoading = false
			}
		},
		onApprove() {
			this.handleApproveSubmit()
		},
		openDeclineModal() {
			if (this.submitLoading) return
			this.showDeclineModal = true
		},
		onConfirmDecline() {
			this.showDeclineModal = false
			this.handleDeclineSubmit()
		},
		renderInvalidLinkState() {
			this.consentData = {
				...this.consentData,
				status: 'CONSENT_LINK_INVALIDATED'
			}
			this.snackbarActive = false
		},
		showSnackbar(text: string, type: SnackbarType = 'info') {
			this.snackbarText = text
			this.snackbarType = type
			this.snackbarActive = true
		}
	}
})
